import { useCallback, useRef, useState } from "react";
import { Landmark, Loader2, ShieldCheck } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { matchesAccept, useFileDrop } from "@/hooks/useFileDrop";
import { gbp, longDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PrimaryButton, SecondaryButton, SectionTitle } from "@/components/shared/ui";
import { FileDropOverlay } from "@/components/shared/FileDropOverlay";
import { LinkLauncher } from "@/components/applicant/BankConnect";
import { ExplainBox, SavedNote } from "@/components/applicant/ExplainBox";
import { LaterLink, useUpload } from "@/components/applicant/DocumentItem";
import { StepActions, StepTitle } from "@/components/applicant/QuestionStep";
import { ErrorBox, FixBox, PassBox } from "@/components/applicant/stageUi";

const money = (n) => gbp(n, n % 1 ? 2 : 0);
const JSON_TYPES = "application/json,.json";
const PDF_TYPES = "application/pdf";

const Payments = ({ payments }) =>
  payments?.length ? (
    <ul className="mt-3 divide-y rounded-xl border bg-background" data-testid="bank-payments">
      {payments.map((p, i) => (
        <li key={i} className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm" data-testid={`bank-payment-${i}`}>
          <span>Payslip {longDate(p.pay_date)} · take-home {money(p.net)}</span>
          <span className={cn("whitespace-nowrap font-medium tabular-nums", p.status === "matched" ? "text-success" : "text-warning")}>
            {p.status === "matched" || p.status === "amount_gap" ? `Paid ${money(p.amount)}` : "Not found"}
          </span>
        </li>
      ))}
    </ul>
  ) : null;

const Fallback = ({ busy, onJsonPick, onPdfPick, loadSample }) => (
  <div className="rise mt-2 flex flex-col gap-2 rounded-2xl border bg-background p-5" data-testid="bank-fallback">
    <SectionTitle>Use a transactions file instead</SectionTitle>
    <p className="text-sm text-muted-foreground">The same checks run on the file. Test data only.</p>
    <SecondaryButton size="lg" onClick={onJsonPick} disabled={busy} testid="bank-file-button">Upload a transactions file (.json)</SecondaryButton>
    <SecondaryButton size="lg" onClick={() => loadSample("applicant_a")} disabled={busy} testid="bank-sample-a">Use test file A (Acme Ltd, £3,120)</SecondaryButton>
    <SecondaryButton size="lg" onClick={() => loadSample("applicant_b")} disabled={busy} testid="bank-sample-b">Use test file B (Acme Ltd, £2,900)</SecondaryButton>
    <SecondaryButton size="lg" onClick={onPdfPick} disabled={busy} testid="bank-pdf-button">Upload bank statements instead</SecondaryButton>
  </div>
);

/** Stage 4. Plaid sandbox, or a synthetic transactions file; the same rules.py checks run on both. */
export const BankStage = ({ data, token, reload, onNext, onLater, single }) => {
  const item = data.checklist.find((i) => i.key === "bank_statements");
  const latest = item?.documents[item.documents.length - 1];
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fallback, setFallback] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const fileInput = useRef(null);
  const pdfInput = useRef(null);
  const pdf = useUpload(token, "bank_statements", reload);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const connect = () => run(async () => setLinkToken((await api.post(`/a/${token}/bank/link-token`)).data.link_token));
  const onSuccess = useCallback(async (publicToken) => {
    setLinkToken(null);
    await run(() => api.post(`/a/${token}/bank/exchange`, { public_token: publicToken }));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadSample = (sample) => run(() => api.post(`/a/${token}/bank/sample`, { sample }));
  const sendJson = (f) => {
    const body = new FormData();
    body.append("file", f);
    run(() => api.post(`/a/${token}/bank/file`, body));
  };
  const onJson = (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (f) sendJson(f);
  };
  const onPdf = (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (f) pdf.send(f);
  };
  const drop = useFileDrop({
    page: true, accept: `${PDF_TYPES},${JSON_TYPES}`, disabled: !item || busy || pdf.progress !== null || latest?.status === "checking" || explaining || !!linkToken,
    onFiles: ([f]) => (matchesAccept(f, JSON_TYPES) ? sendJson(f) : pdf.send(f)),
    onReject: () => setError("That type of file won't work here. Upload your bank statements as a PDF."),
  });

  if (!item) return null;
  const checking = latest?.status === "checking" || pdf.progress !== null;
  const passed = latest?.status === "accepted";
  const flagged = latest && ["needs_review", "needs_fixing"].includes(latest.status);
  const isConnection = latest?.doc_kind === "bank_connection";
  const canContinue = item.complete || item.explained;

  return (
    <div data-testid="stage-bank">
      <FileDropOverlay show={drop.dragging} title="Drop to upload your bank statements" text="A PDF statement from your bank." testid="bank-drop-overlay" />
      {!single && (
        <StepTitle sub={item.detail} testid="bank-title">{item.title}</StepTitle>
      )}
      <div className="mt-6 flex items-start gap-3 rounded-xl bg-background p-4 ring-1 ring-border" data-testid="bank-explainer">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-success" />
        <p className="text-base font-medium leading-snug">We only look for your pay. The agent never sees your spending.</p>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">We read the last 90 days and match each payment to your payslips: the same amount (within {data.let.bank_tolerance_pct}%) and within {data.let.bank_tolerance_days} days of the pay date. {data.let.flag_gambling && "For this property we also count gambling transactions and missed-payment fees. Only the counts are kept."}</p>

      {checking && (
        <p className="mt-4 flex items-center gap-2 rounded-lg bg-brand/40 p-3 text-sm font-medium dark:bg-brand/10" data-testid="bank-checking">
          <Loader2 className="size-4 animate-spin" /> Checking your payments…
        </p>
      )}
      {latest && !checking && passed && (
        <div className="mt-4" data-testid="bank-result-pass">
          <PassBox><span className="font-medium" data-testid="bank-result-summary">{latest.summary || "Bank statements accepted"}</span></PassBox>
          {isConnection && <Payments payments={latest.payments} />}
        </div>
      )}
      {latest && !checking && flagged && (
        <div className="mt-4" data-testid="bank-result-flag">
          <FixBox title={latest.status === "needs_review" ? "Needs review" : "Needs fixing"}><span data-testid="bank-result-message">{latest.fix_message}</span></FixBox>
          {isConnection && <Payments payments={latest.payments} />}
          <SavedNote note={latest.note} testid="bank-note" />
        </div>
      )}
      {latest?.status === "error" && !checking && <ErrorBox className="mt-4" testid="bank-error">{latest.error_message}</ErrorBox>}
      {(error || pdf.error) && <ErrorBox className="mt-4" testid="bank-action-error">{error || pdf.error}</ErrorBox>}

      {linkToken && <div className="mt-4"><LinkLauncher token={linkToken} onSuccess={onSuccess} onExit={() => setLinkToken(null)} /></div>}

      {explaining ? (
        <ExplainBox token={token} docId={latest.id} note={latest.note} onSaved={reload} onClose={() => setExplaining(false)} testid="bank-explain" />
      ) : (
        <StepActions className={single ? "static mx-0 bg-none px-0 pb-0" : undefined}>
          {canContinue && !single && <PrimaryButton size="lg" onClick={onNext} testid="bank-continue">Continue</PrimaryButton>}
          {flagged && <SecondaryButton size="lg" onClick={() => setExplaining(true)} testid="bank-explain-button">{latest.note ? "Edit my note" : "Explain"}</SecondaryButton>}
          {!passed && (
            <PrimaryButton size="xl" className="w-full" onClick={connect} isLoading={busy} disabled={checking} testid="bank-connect-button">
              {!busy && <Landmark />} {latest ? "Connect a different account" : "Connect your bank"}
            </PrimaryButton>
          )}
          <button type="button" onClick={() => setFallback((v) => !v)} className="w-full py-2 text-center text-sm font-medium text-muted-foreground underline underline-offset-4 hover:text-foreground" data-testid="bank-cant-connect">Can't connect?</button>
          {!canContinue && !checking && onLater && <LaterLink onClick={onLater} testid="item-later-bank_statements" />}
        </StepActions>
      )}

      {fallback && !explaining && (
        <Fallback busy={busy || checking} loadSample={loadSample} onJsonPick={() => fileInput.current?.click()} onPdfPick={() => pdfInput.current?.click()} />
      )}
      <input ref={fileInput} type="file" accept={JSON_TYPES} className="hidden" onChange={onJson} data-testid="bank-file-input" />
      <input ref={pdfInput} type="file" accept={PDF_TYPES} className="hidden" onChange={onPdf} data-testid="bank-pdf-input" />
    </div>
  );
};
